import { Request } from '../utils/request';
import type { CommonResponse, EmptyResponse } from '../types/responseModal';

interface ProcessInfo {
  cmdline: string[];
  cpu_percent: string;
  create_time: string;
  memory_percent: string;
  pid: string;
}

export interface InferenceConfigRes {
  backend: 'mlx' | 'llama' | 'vllm';
  llm_service_url: string;
  mlx_controller_url?: string;
  mlx_server_port?: number;
  in_docker?: boolean;
  lora_model_id?: string;
  remote_vllm_model?: string;
  remote_vllm_url?: string;
  remote_lora_path?: string;
  remote_lora_registered?: boolean;
  xiaogong_default_provider?: 'vllm' | 'bailian';
  xiaogong_providers?: Array<'vllm' | 'bailian'>;
  fangzi_default_provider?: 'vllm' | 'bailian';
  fangzi_providers?: Array<'vllm' | 'bailian'>;
  bailian_configured?: boolean;
  bailian_base_url?: string;
  bailian_model_xiaogong?: string;
  bailian_model_fangzi?: string;
  bailian_model_fangzi_teach?: string;
}

export interface ServiceStatusRes {
  process_info?: ProcessInfo;
  is_running?: boolean;
  backend?: 'mlx' | 'llama' | 'vllm';
  llm_service_url?: string;
  remote_model?: string;
  lora_model_id?: string;
  remote_lora_registered?: boolean;
  remote_lora_path?: string;
}

interface StartTrainResponse {
  progress_id: string;
}

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'suspended';
export type StageStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'suspended';

interface TrainStep {
  completed: boolean;
  name: string;
  status: StepStatus;
  path?: string;
  have_output?: boolean;
}

interface TrainStage {
  name: string;
  progress: number;
  status: StageStatus;
  steps: TrainStep[];
  current_step: string | null;
}
export interface TrainStepJson {
  content: object[];
  file_type: 'json';
}

export interface TrainStepParquet {
  columns: string[];
  content: object[];
  file_type: 'parquet';
}
export type TrainStepOutput = TrainStepJson | TrainStepParquet;

export type StageName =
  | 'downloading_the_base_model'
  | 'activating_the_memory_matrix'
  | 'synthesize_your_life_narrative'
  | 'prepare_training_data_for_deep_comprehension'
  | 'training_to_create_second_me';

export type StageDisplayName =
  | 'Downloading the Base Model'
  | 'Activating the Memory Matrix'
  | 'Synthesize Your Life Narrative'
  | 'Prepare Training Data for Deep Comprehension'
  | 'Training to create Second Me';

export interface TrainProgress {
  stages: TrainStage[];
  overall_progress: number;
  current_stage: StageName;
  status: StageStatus;
}

export interface TrainAdvanceParams {
  is_cot?: boolean;
}

export interface TrainingParams {
  concurrency_threads?: number;
  data_synthesis_mode?: string;
  learning_rate?: number;
  number_of_epochs?: number;
  use_cuda?: boolean;
}

export interface TrainBaseParams {
  model_name: string;
}

export type TrainingConfig = TrainingParams & TrainAdvanceParams & TrainBaseParams;

export const startTrain = (config: TrainingConfig) => {
  return Request<CommonResponse<StartTrainResponse>>({
    method: 'post',
    url: '/api/trainprocess/start',
    data: config
  });
};

export const getTrainProgress = (config: TrainingConfig) => {
  return Request<CommonResponse<TrainProgress>>({
    method: 'get',
    url: `/api/trainprocess/progress/${config.model_name}`
  });
};

export const resetProgress = () => {
  return Request<CommonResponse<EmptyResponse>>({
    method: 'post',
    url: `/api/trainprocess/progress/reset`
  });
};

export const stopTrain = () => {
  return Request<CommonResponse<EmptyResponse>>({
    method: 'post',
    url: `/api/trainprocess/stop`
  });
};

export const retrain = (config: TrainingConfig) => {
  return Request<CommonResponse<EmptyResponse>>({
    method: 'post',
    url: `/api/trainprocess/retrain`,
    data: config
  });
};

export const startService = (config: TrainingConfig) => {
  return Request<CommonResponse<EmptyResponse>>({
    method: 'post',
    url: `/api/kernel2/llama/start`,
    data: config
  });
};

export const getServiceStatus = () => {
  return Request<CommonResponse<ServiceStatusRes>>({
    method: 'get',
    url: `/api/kernel2/llama/status`
  });
};

export const getInferenceConfig = () => {
  return Request<CommonResponse<InferenceConfigRes>>({
    method: 'get',
    url: `/api/kernel2/inference/config`
  });
};

export const stopService = () => {
  return Request<CommonResponse<EmptyResponse>>({
    method: 'post',
    url: `/api/kernel2/llama/stop`
  });
};

export const getTrainingParams = () => {
  return Request<CommonResponse<TrainingConfig>>({
    method: 'get',
    url: `/api/trainprocess/training_params`
  });
};

export const checkCudaAvailability = () => {
  return Request<
    CommonResponse<{
      cuda_available: boolean;
      cuda_info: {
        device_count?: number;
        current_device?: number;
        device_name?: string;
      };
    }>
  >({
    method: 'get',
    url: '/api/kernel2/cuda/available'
  });
};

export const getStepOutputContent = (stepName: string) => {
  return Request<CommonResponse<TrainStepOutput>>({
    method: 'get',
    url: `/api/trainprocess/step_output_content?step_name=${stepName}`
  });
};

export interface VectorizeStatusRes {
  in_progress: boolean;
  embeddings: {
    total_documents?: number;
    documents_with_embeddings?: number;
    documents_with_chunk_embeddings?: number;
    all_complete?: boolean;
    [key: string]: unknown;
  };
}

export const startVectorize = (options?: {
  include_l1?: boolean;
  model_name?: string;
  force_rebuild?: boolean;
}) => {
  return Request<CommonResponse<{ model_name: string; include_l1: boolean; force_rebuild?: boolean }>>({
    method: 'post',
    url: '/api/trainprocess/vectorize',
    data: options ?? {}
  });
};

export const getVectorizeStatus = () => {
  return Request<CommonResponse<VectorizeStatusRes>>({
    method: 'get',
    url: '/api/trainprocess/vectorize/status'
  });
};
